import {
  Avatar,
  Badge,
  Box,
  Card,
  Chip,
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  ListItemAvatar,
  ListItemButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CTLNotification from "application/components/CTLNotification";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import MDTypography from "components/MDTypography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import ClearIcon from "@material-ui/icons/Clear";
import { FixedSizeList as VirtualizedList } from "react-window";
import { useMaterialUIController } from "context";
import theme from "assets/theme";

const ListOuter = React.forwardRef(function ListOuter(props, ref) {
  return (
    <div
      ref={ref}
      {...props}
      className={`no-scrollbar ${props.className || ""}`}
      style={{
        ...props.style,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {props.children}
    </div>
  );
});

function UserAndGroupList({
  setSelectUser,
  setSelectGroup,
  selectGroup,
  selectUser,
  isCompactScreen = false,
  contacts = [],
  contactsLoading = false,
  onRefreshContacts,
}) {
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(300);
  const [groupList, setGroupList] = useState(() => AuthenticationService.getDepartmentList() || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("Group");
  const [chatNotifications, setChatNotifications] = useState({});
    const [controller, dispatch] = useMaterialUIController();
    const { darkMode, dispatcherEvent } = controller;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  // Load notifications from localStorage
  useEffect(() => {
    const loadNotifications = () => {
      try {
        const stored = localStorage.getItem("chat-notifications-by-user");
        if (stored) {
          const parsed = JSON.parse(stored);
          setChatNotifications(prev => {
            // Only update if changed to avoid unnecessary re-renders
            if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
              console.log("Notifications updated:", parsed);
              return parsed;
            }
            return prev;
          });
        }
      } catch (e) {
        console.error("Error loading notifications:", e);
      }
    };
    
    // Load on mount
    loadNotifications();
    
    // Listen to custom event from sidebar
    const handleNotificationUpdate = (event) => {
      console.log("Custom event received:", event);
      loadNotifications();
    };
    
    // Listen to storage changes (cross-tab)
    const handleStorageChange = (event) => {
      if (event.key === "chat-notifications-by-user") {
        console.log("Storage change detected");
        loadNotifications();
      }
    };
    
    // Polling fallback
    const interval = setInterval(loadNotifications, 1000);
    
    // Add listeners
    window.addEventListener("chat-notification-updated", handleNotificationUpdate);
    window.addEventListener("storage", handleStorageChange);
    
    // Cleanup
    return () => {
      clearInterval(interval);
      window.removeEventListener("chat-notification-updated", handleNotificationUpdate);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Get notification count for user/group
  const getNotificationCount = (item, type) => {
    if (type === "group") {
      // For groups, use extension field
      return chatNotifications[item.extension]?.count || 0;
    } else {
      // For users, use offEXtn field
      return chatNotifications[item.offEXtn]?.count || 0;
    }
  };

  // Clear notifications for specific user/group
  const clearNotifications = (identifier) => {
    console.log("Clearing notifications for:", identifier);
    console.log("Current notifications before clear:", chatNotifications);
    if (window.clearChatNotification) {
      window.clearChatNotification(identifier);
      // Update local state immediately
      setChatNotifications(prev => {
        const updated = { ...prev };
        delete updated[identifier];
        console.log("Notifications after clear:", updated);
        return updated;
      });
    }
  };


  useEffect(() => {
    const measureHeight = () => {
      if (listContainerRef.current) {
        const h = listContainerRef.current.clientHeight;
        if (h > 0) setListHeight(h);
      }
    };
    measureHeight();
    window.addEventListener("resize", measureHeight);
    return () => window.removeEventListener("resize", measureHeight);
  }, [isCompactScreen]);

  const handleRefreshContact = () => {
    if (typeof onRefreshContacts === "function") {
      onRefreshContacts();
    }
    setNotify({
      isOpen: true,
      message: "",
      type: "success",
      pagename: "Chat",
      status: "Contact Refresh Successfully",
    });
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
    setSelectGroup("");
    setSelectUser("");
  };

  const fullName = AuthenticationService.getFullName();

  const filteredList = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = contacts.filter((item) => {
      const name = item?.designation || item?.name || "";
      const ext = String(item?.offEXtn || "");
      return (
        name.toLowerCase() !== fullName.toLowerCase() &&
        (name.toLowerCase().includes(q) || ext.includes(q))
      );
    });
    // Sort by notification count
    return filtered.sort((a, b) => {
      const countA = getNotificationCount(a, "user");
      const countB = getNotificationCount(b, "user");
      if (countB !== countA) return countB - countA;
      const nameA = String(a?.designation ?? a?.name ?? "");
      const nameB = String(b?.designation ?? b?.name ?? "");
      return nameA.localeCompare(nameB);
    });
  }, [contacts, searchQuery, chatNotifications]);

  const filteredGroupList = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = groupList.filter((item) => {
      const name = item?.departmentDescription || "";
      const ext = String(item?.extension || "");
      return name.toLowerCase().includes(q) || ext.includes(q);
    });
    // Sort by notification count
    return filtered.sort((a, b) => {
      const countA = getNotificationCount(a, "group");
      const countB = getNotificationCount(b, "group");
      if (countB !== countA) return countB - countA;
      const nameA = String(a?.departmentDescription ?? "");
      const nameB = String(b?.departmentDescription ?? "");
      return nameA.localeCompare(nameB);
    });
  }, [groupList, searchQuery, chatNotifications]);

  // Calculate total notification count for groups
  const totalGroupNotifications = useMemo(() => {
    return groupList.reduce((total, item) => {
      return total + (chatNotifications[item.extension]?.count || 0);
    }, 0);
  }, [groupList, chatNotifications]);

  // Calculate total notification count for users
  const totalUserNotifications = useMemo(() => {
    return contacts.reduce((total, item) => {
      return total + (chatNotifications[item.offEXtn]?.count || 0);
    }, 0);
  }, [contacts, chatNotifications]);

  const renderRow = ({ index, style, data }) => {
    const { list, type } = data;
    const item = list[index];

    if (!item) return null;

    const label =
      type === "user"
        ? item?.designation || item?.name || "Unknown User"
        : item?.departmentDescription || "Unknown Group";

    const extension =
      type === "user" ? item?.offEXtn : item?.extension;

    const notificationCount = getNotificationCount(item, type);
    const isSelected =
      type === "user" ? selectUser === item : selectGroup === item;

    return (
      <Box style={style} sx={{ px: 0.4, py: 0.12 }}>
        <Card
          elevation={0}
          sx={(theme) => ({
            borderRadius: 1.6,
            border: `1px solid ${alpha(theme.palette.info.main, isSelected ? 0.32 : 0.14)}`,
            backgroundColor: isSelected
              ? alpha(theme.palette.info.main, 0.10)
              : "transparent",
            boxShadow: isSelected
              ? `0 6px 18px ${alpha(theme.palette.info.main, 0.16)}`
              : `0 2px 10px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.2 : 0.06)}`,
            transition: "all 180ms ease",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: alpha(theme.palette.info.main, 0.26),
              boxShadow: `0 8px 20px ${alpha(theme.palette.info.main, 0.14)}`,
            },
          })}
        >
          <ListItemButton
            onClick={() => {
              if (type === "user") {
                if (selectUser !== item) {
                  setSelectGroup("");
                  setSelectUser(item);
                }
                clearNotifications(item.offEXtn);
              } else {
                if (selectGroup !== item) {
                  setSelectUser("");
                  setSelectGroup(item);
                }
                clearNotifications(item.extension);
              }
            }}
            selected={isSelected}
            sx={(theme) => ({
              borderRadius: 1.6,
              py: isCompactScreen ? 0.45 : 0.55,
              px: isCompactScreen ? 0.65 : 0.85,
              display: "flex",
              alignItems: "center",
              minHeight: isCompactScreen ? 48 : 54,
              gap: isCompactScreen ? 0.45 : 0.65,
              transition: "all 180ms ease",
              "&.Mui-selected": {
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: "transparent",
                },
                "& .list-avatar": {
                  bgcolor: theme.palette.info.main,
                  color: theme.palette.info.contrastText,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.info.main, 0.35)}`,
                },
                "& .list-label": {
                  color: theme.palette.info.main,
                  fontWeight: 700,
                },
                "& .list-chip": {
                  borderColor: alpha(theme.palette.info.main, 0.45),
                  backgroundColor: alpha(theme.palette.info.main, 0.08),
                },
              },
              "&:hover": {
                bgcolor: "transparent",
              },
            })}
          >
            <ListItemAvatar sx={{ minWidth: isCompactScreen ? 34 : 42 }}>
              <Badge
                badgeContent={notificationCount}
                color="error"
                max={99}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.5rem",
                    minWidth: 14,
                    height: 14,
                    p: "0 3px",
                  },
                }}
              >
                <Avatar
                  className="list-avatar"
                  sx={(theme) => ({
                    width: isCompactScreen ? 28 : 34,
                    height: isCompactScreen ? 28 : 34,
                    bgcolor: alpha(theme.palette.info.main, 0.16),
                    color: theme.palette.info.main,
                    border: `1.5px solid ${alpha(theme.palette.info.main, 0.28)}`,
                    transition: "all 180ms ease",
                  })}
                >
                  {type === "user" ? (
                    <PersonIcon sx={{ fontSize: isCompactScreen ? 14 : 17 }} />
                  ) : (
                    <GroupIcon sx={{ fontSize: isCompactScreen ? 14 : 17 }} />
                  )}
                </Avatar>
              </Badge>
            </ListItemAvatar>

            <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
              <Tooltip title={label || ""} placement="top" arrow>
                <MDTypography
                  className="list-label"
                  variant="caption"
                  fontWeight="medium"
                  noWrap
                  sx={{
                    display: "block",
                    fontSize: isCompactScreen ? "0.64rem" : "0.72rem",
                    lineHeight: 1.2,
                    transition: "color 180ms ease",
                  }}
                >
                  {label}
                </MDTypography>
              </Tooltip>
              {extension && (
                <Chip
                  className="list-chip"
                  label={extension}
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{
                    height: 16,
                    fontSize: "0.56rem",
                    mt: 0.35,
                    borderRadius: 1,
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />
              )}
            </Box>
          </ListItemButton>
        </Card>
      </Box>
    );
  };

  return (
    <Card
      sx={(theme) => ({
        width: isCompactScreen ? 200 : 400,
        height: "100%",
        borderRight: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        p: isCompactScreen ? 0.5 : 0.75,
        borderRadius: 1.5,
        // bgcolor: theme.palette.background.default,
        boxShadow: theme.shadows[4],
        minHeight: 0,
      })}
    >
      {/* Sticky Header */}
      <Card
        sx={(theme) => ({
          borderRadius: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 1,
          // bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Tabs
          value={selectedTab}
          onChange={handleChangeTab}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: (theme) => theme.palette.info.main,
              height: 2,
            },
            minHeight: isCompactScreen ? 30 : 36,
            "& .MuiTab-root": {
              minHeight: isCompactScreen ? 30 : 36,
              fontSize: isCompactScreen ? "0.52rem" : "0.62rem",
              textTransform: "none",
              px: isCompactScreen ? 0.2 : 0.5,
              py: 0,
            },
          }}
        >
          <Tab
            label={
              <Badge
                badgeContent={totalGroupNotifications}
                color="error"
                max={99}
                sx={{
                  pr: totalGroupNotifications > 0 ? 1.6 : 0,
                  "& .MuiBadge-badge": {
                    fontSize: "0.48rem",
                    minWidth: 13,
                    height: 13,
                  },
                }}
              >
                Group
              </Badge>
            }
            value="Group"
          />
          <Tab
            label={
              <Badge
                badgeContent={totalUserNotifications}
                color="error"
                max={99}
                sx={{
                  pr: totalUserNotifications > 0 ? 1.6 : 0,
                  "& .MuiBadge-badge": {
                    fontSize: "0.48rem",
                    minWidth: 13,
                    height: 13,
                  },
                }}
              >
                User
              </Badge>
            }
            value="User"
          />
        </Tabs>
      </Card>

      <TextField
        label={`Search ${selectedTab}`}
        variant="outlined"
        size="small"
        fullWidth
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoComplete="off"
        sx={(theme) => ({
          mt: 0.5,
          "& .MuiInputLabel-root": {
            fontSize: isCompactScreen ? "0.62rem" : "0.68rem",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.5,
            minHeight: isCompactScreen ? 32 : 36,
            // bgcolor: theme.palette.background.default,
          },
          "& .MuiOutlinedInput-input": {
            fontSize: isCompactScreen ? "0.66rem" : "0.72rem",
            py: isCompactScreen ? 0.6 : 0.7,
          },
        })}
        InputProps={{
          endAdornment: (
            <>
              {searchQuery && (
                <Tooltip title="Clear">
                  <IconButton size="small" sx={{ p: 0.35 }} onClick={() => setSearchQuery("")}>
                    <ClearIcon color="error" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <InputAdornment position="end">
                <Tooltip title="Refresh Contact">
                  <IconButton size="small" sx={{ p: 0.35 }} onClick={handleRefreshContact}>
                    <Icon sx={{ fontSize: 12 , color: "info.main" }}>
                      refresh_icon
                    </Icon>
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            </>
          ),
        }}
      />

      {/* Virtualized List */}
      <Box
        ref={listContainerRef}
        sx={(theme) => ({
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          mt: 0.5,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          "& .no-scrollbar::-webkit-scrollbar": {
            width: 0,
            height: 0,
          },
        })}
      >
        {contactsLoading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              backdropFilter: "blur(4px)",
             backgroundColor:  darkMode ? theme.palette.background.paper + "cc" : theme.palette.common.white + "cc",
                                
              zIndex: 2,
              borderRadius: 1.5,
            }}
          >
            <CircularProgress size={28} color="info" />
            <MDTypography
              variant="caption"
              sx={{ fontWeight: 600, color: "text", fontSize: "0.7rem" }}
            >
              Refreshing contacts…
            </MDTypography>
          </Box>
        ) : null}
        <VirtualizedList
          height={listHeight}
          width="100%"
          itemSize={isCompactScreen ? 56 : 60}
          itemCount={
            selectedTab === "Group"
              ? filteredGroupList.length
              : filteredList.length
          }
          itemData={{
            list: selectedTab === "Group" ? filteredGroupList : filteredList,
            type: selectedTab === "Group" ? "group" : "user",
          }}
          outerElementType={ListOuter}
        >
          {renderRow}
        </VirtualizedList>
      </Box>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Card>
  );
}

export default UserAndGroupList;
